import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import img_store_product_image from '@/assets/images/store-product-image.png';
import { madeToOrderOverrides } from './madeToOrderOverrides';

function galleryEntryUrl(entry) {
  if (entry == null) return undefined;
  if (typeof entry === 'string') return entry;
  return entry.url ?? entry.src;
}

/**
 * 列表 / 卡片首图：使用 `product_galleries_normal_img` 的第一张。
 * 若仅有旧字段 `product_galleries`，则取第一个非视频图（兼容未迁移数据）。
 */
export function firstGalleryImageUrl(product) {
  const normal = product?.product_galleries_normal_img;
  if (Array.isArray(normal) && normal.length > 0) {
    const href = galleryEntryUrl(normal[0]);
    if (href != null && href !== '') return href;
  }
  const items = product?.product_galleries;
  if (!items?.length) return undefined;
  for (const g of items) {
    const t = String(g?.type ?? '').toLowerCase();
    if (t === 'video') continue;
    if (t !== 'image') continue;
    const href = g?.url ?? g?.src;
    if (href != null && href !== '') return href;
  }
  return undefined;
}

/**
 * 详情页轮播顺序：室内设计图 → 视频 → 普通产品图。
 * 视频的 `poster`：条目自带，否则用第一张普通产品图。
 * 无新分栏时回退到旧字段 `product_galleries`。
 */
export function mergeProductGalleriesForDetail(product) {
  const interior = product?.product_galleries_interior_img;
  const normal = product?.product_galleries_normal_img;
  const videos = product?.product_galleries_video;
  const usesSplitFields =
    Array.isArray(interior) || Array.isArray(normal) || Array.isArray(videos);

  if (!usesSplitFields) {
    return product?.product_galleries ?? [];
  }

  const fallbackPoster = galleryEntryUrl(normal?.[0]);
  const out = [];

  for (const item of interior ?? []) {
    const url = galleryEntryUrl(item);
    if (url) out.push({ type: 'image', url });
  }
  for (const item of videos ?? []) {
    const url = galleryEntryUrl(item);
    if (!url) continue;
    const poster =
      item?.poster !== undefined && item?.poster !== null && item.poster !== ''
        ? item.poster
        : fallbackPoster;
    out.push({
      type: 'video',
      url,
      ...(poster != null && poster !== '' ? { poster } : {}),
    });
  }
  for (const item of normal ?? []) {
    const url = galleryEntryUrl(item);
    if (url) out.push({ type: 'image', url });
  }

  return out;
}

/**
 * Value for React `style={{ backgroundImage: ... }}` when URL may contain spaces (e.g. Vite dev).
 * Unquoted `url(...)` in CSS breaks on spaces; quoted `url("...")` is safe.
 */
export function cssBackgroundImageUrl(href) {
  if (href == null || href === '') return undefined;
  const s = String(href).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `url("${s}")`;
}

/** Display label → URL segment (remove spaces, PascalCase). */
export function toRouteKey(label) {
  if (!label || typeof label !== 'string') return '';
  return label
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

function withRouteKeys(collection) {
  const routeKey = toRouteKey(collection.collection_name);
  const products = (collection.products ?? []).map(product => ({
    ...product,
    ...(product.sku ? madeToOrderOverrides[product.sku] : null),
    routeKey: toRouteKey(product.product_name),
  }));
  return { ...collection, routeKey, products };
}

/**
 * 在 ./collections 下新增模块（默认导出系列对象）即会加入列表。
 * 可选导出 `order`（number）：越小越靠前；未导出时 order 视为 999，再按文件路径排序。
 */
const collectionModules = import.meta.glob('./collections/*.js', { eager: true });

const collections = Object.entries(collectionModules)
  .map(([path, mod]) => ({
    path,
    order: typeof mod.order === 'number' ? mod.order : 999,
    data: mod.default,
  }))
  .filter(entry => entry.data != null)
  .sort((a, b) => a.order - b.order || a.path.localeCompare(b.path))
  .map(entry => withRouteKeys(entry.data));

export const useProductStore = create()(
  devtools(
    set => ({
      collections,
      others: [
        {
          title: 'Custom Flooring',
          desc: 'We specialize in manufacturing high-quality custom hardwood flooring for distributors, contractors, designers, and developers. With flexible production capabilities, we can produce flooring from virtually any wood species to meet your project’s design, performance, and budget requirements.\n\nWhether you are sourcing flooring for residential homes, commercial spaces, or large development projects, we deliver consistent quality, reliable lead times, and competitive pricing.',
          image: img_store_product_image,
          link: '/products/custom',
        },
      ],
    }),
    { name: 'productStore' }
  )
);
